import { CONTROL, Disclosure, Hint, type ScrubProps } from '@/components/builder/inspector-controls';
import { ColorMintPopover } from '@/components/builder/color-mint';
import { FontVariantPopover } from '@/components/builder/font-variant';
import { OpenThemeTokenLink, ThemeTokenSample } from '@/components/builder/token-navigation';
import { sizeOptions, type SizeMode, type StepCtx } from '@/components/builder/validate';
import { cn } from '@/lib/cn';
import type { AuthoringBlockNode as BlockNode } from '@personalizehq/schemas';
import type { FontToken, Padding, Size, SizeVal, Span, Theme } from '@personalizehq/schemas/ir';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { Check, ChevronDown, Link2, Maximize2, Minimize2, Plus, Ruler, Unlink2, X, type LucideIcon } from 'lucide-react';
import { useState } from 'react';

// The inspector's widgets. RADIX, where this file once said "native rather than Radix" — and the old
// reasoning is worth keeping, because only half of it turned out to be wrong.
//
// It was right that the natives are keyboard- and screen-reader-correct for free, and that a dense
// panel cannot afford a widget that needs a pointer-capture shim to be testable. Radix satisfies both:
// every role survives the swap (`combobox`, `radio`, `slider`, `spinbutton`), and the inventory golden
// proves it — the whole migration moved exactly 16 lines, all of them `checkbox:` → `switch:`.
//
// It was WRONG that native was therefore the right choice, because of one thing it never weighed: a
// native `<option>` CANNOT CARRY A GLYPH. So the panel's enums could only ever show words — and this
// is a panel where `fit: "fill"` (stretch the pixels) sits three rows from a size `fill` (take the
// leftover space), and where the signature control is a COLOUR PICKER that was rendering the *name*
// of a colour. The dropdown could not do its job, and no amount of styling was going to fix that.
//
// `<input type="number">` STAYS NATIVE. `useScrub` drags the LABEL precisely so the input underneath
// stays a plain `spinbutton` — that is load-bearing, and it is why the number fields are the one
// widget here that did not move.

/** THE row. A fixed 84px label column (Plasmic's `$label-col-width`), a "set here" dot, right-click
 *  to unset, and an optional scrub handle on the label itself.
 *
 *  Plasmic's dot distinguishes eight inheritance/override sources and opens a source/reset menu.
 *  We have no inheritance stack, so showing the same dot would imply meaning that is not here.
 *  `set` remains data for progressive disclosure and the existing reset action only. */
export function Field({
  label,
  error,
  set,
  onUnset,
  scrub,
  inert,
  note,
  stacked,
  children,
}: {
  label: string;
  error?: string | undefined;
  /** Is the key actually PRESENT on the node? Drives `Disclosure` and the reset action. */
  set?: boolean | undefined;
  onUnset?: (() => void) | undefined;
  scrub?: ScrubProps | undefined;
  /** WHY THIS FIELD DOES NOTHING RIGHT NOW — the audit's highest-value panel fix (C2).
   *
   *  The commonest bug shape in this product: a field is SET, shows a set-dot, and is silently
   *  ignored because ANOTHER field's value neutered it (`aspect_ratio` under an explicit height;
   *  `justify` on a hugging main axis). The value is still on the node and still publishes — so the
   *  row must not vanish, and the control must not lie. It greys, keeps its value, and SAYS WHICH
   *  OTHER FIELD did it. Naming the culprit is the whole point: "disabled" alone leaves the author
   *  with nothing to change. */
  inert?: string | undefined;
  /** WHAT THE VALUE RESOLVES TO — one muted line under the control, aligned to the control column so
   *  it reads as belonging to the value rather than to the label.
   *
   *  Read-only, always. The panel's token fields hold a NAME (`body`, `text.primary`) and the author
   *  has no way to see what it means without leaving for the Theme panel; this is that answer, in
   *  place. It must never look editable, because the value is the TOKEN'S and editing it here would
   *  change every block using it. */
  note?: string | undefined;
  /** Spatial editors need the whole inspector width. Typography and scalar fields keep the fixed
   *  label rail; alignment matrices and padding diagrams put their label above the control. */
  stacked?: boolean | undefined;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      data-row=""
      data-set={set || undefined}
      className="relative space-y-1"
      onContextMenu={
        onUnset &&
        ((e) => {
          e.preventDefault();
          onUnset();
        })
      }
    >
      <div
        data-field-main=""
        className={cn(
          'grid items-center',
          stacked ? 'grid-cols-1 gap-y-1.5' : 'grid-cols-[84px_minmax(0,1fr)] gap-x-2'
        )}
      >
        <label
          {...scrub}
          title={scrub ? 'drag to change' : undefined}
          className={cn(
            'flex min-w-0 items-center text-[12px] transition-opacity',
            stacked ? 'text-muted-foreground' : 'text-foreground',
            scrub && 'cursor-ew-resize select-none',
            inert && 'opacity-50'
          )}
        >
          {label}
        </label>
        <div data-field-value="" className="flex min-w-0 items-center justify-start gap-1">
          {children}
          {inert && !stacked ? <Hint>{inert}</Hint> : null}
        </div>
        {inert && stacked ? (
          <span className="absolute right-3 mt-0.5">
            <Hint>{inert}</Hint>
          </span>
        ) : null}
      </div>
      {/* Truncates, never wraps: a two-line note would break the row rhythm for every field below it,
          and the full string is on the `title`. */}
      {note ? (
        <div className="grid grid-cols-[84px_minmax(0,1fr)] gap-x-2">
          <span aria-hidden="true" />
          <p className="truncate text-left text-[11px] text-muted-foreground" title={note}>
            {note}
          </p>
        </div>
      ) : null}
      {/* The law is surfaced AT THE FIELD, the moment it is wrong — never as a list of 14 errors
          after clicking publish (design hand-off §6). */}
      {error ? <p className="text-right text-[11px] text-destructive">{error}</p> : null}
    </div>
  );
}



export function TextInput({
  value,
  onChange,
  className,
  ...props
}: {
  value: string;
  onChange: (value: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>): React.JSX.Element {
  return (
    <input
      {...props}
      className={cn(CONTROL, 'w-full', className)}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function NumberInput({
  value,
  onChange,
  className,
  ...props
}: {
  /** `undefined` renders empty and means "the field is absent" — not zero. */
  value: number | undefined;
  onChange: (value: number | undefined) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>): React.JSX.Element {
  return (
    <input
      {...props}
      type="number"
      className={cn(CONTROL, 'phq-number w-[70px] text-right tabular-nums', className)}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
    />
  );
}

/** THE HEADLINE OF THIS SLICE. A native `<select>` cannot put an ICON in an option — the browser
 *  renders `<option>` text and nothing else — so the panel's enums could never SHOW what they meant.
 *  That is not cosmetic: `fit: "fill"` (stretch the pixels) sits three rows from a size `fill` (take
 *  the leftover space), and reading the bare word is how an author comes to believe `cover` should
 *  push a button down the screen. A glyph settles it at a glance; a dropdown of five identical words
 *  never can.
 *
 *  THE CONTENT IS PORTALLED. The inspector is an `overflow-y-auto` rail, so an in-place popper gets
 *  clipped at the panel edge — most visibly for the image asset picker near the bottom. Testing
 *  Library queries `document.body`, including portals, so the inspector walker still opens and
 *  drives every option. */
export function SelectInput<T extends string>({
  value,
  options,
  labels,
  icons,
  onChange,
  className,
  disabled,
  ...props
}: {
  value: T;
  options: readonly T[];
  /** DISPLAY ONLY. The wire values are pinned in a binary and cannot be renamed — but reading the raw
   *  enum is how an author misreads it. The option's `value` is untouched, so anything walking this
   *  control by value still finds it. */
  labels?: Partial<Record<T, string>> | undefined;
  /** One glyph per value. The reason this control is no longer native. */
  icons?: Partial<Record<T, LucideIcon>> | undefined;
  onChange: (value: T) => void;
  className?: string;
  disabled?: boolean | undefined;
} & Pick<React.HTMLAttributes<HTMLElement>, 'aria-label' | 'title'>): React.JSX.Element {
  // Annotated, not inferred: indexing a `Partial<Record<T, LucideIcon>>` by a generic `T` gives a
  // type JSX cannot call, and `exactOptionalPropertyTypes` will not let the `className` through.
  const Current: LucideIcon | undefined = icons?.[value];

  return (
    <SelectPrimitive.Root
      value={value}
      onValueChange={(next) => onChange(next as T)}
      // Spread, never `disabled={undefined}` — `exactOptionalPropertyTypes` is on, and Radix's prop
      // is a plain `boolean`.
      {...(disabled === undefined ? {} : { disabled })}
    >
      <SelectPrimitive.Trigger
        {...props}
        className={cn(CONTROL, 'flex w-full items-center gap-1.5 data-[placeholder]:text-muted-foreground', className)}
      >
        {Current ? <Current className="size-3.5 shrink-0 text-muted-foreground" /> : null}
        {/* TRUNCATE, NEVER WRAP. A long option ("cover — crop to fill") wrapped to two lines and made
            its row half again as tall as its neighbours — one control out of rhythm is all it takes
            for a dense panel to read as sloppy. */}
        <SelectPrimitive.Value className="truncate">{labels?.[value] ?? value}</SelectPrimitive.Value>
        <SelectPrimitive.Icon className="ml-auto shrink-0">
          <ChevronDown className="size-3 text-muted-foreground" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          align="end"
          className="z-50 min-w-[--radix-select-trigger-width] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          <SelectPrimitive.Viewport>
            {options.map((option) => {
              const Icon: LucideIcon | undefined = icons?.[option];
              return (
                <SelectPrimitive.Item
                  key={option}
                  value={option}
                  // Radix does NOT put the value on the element, and `labels` means the visible text is
                  // often not it (`fit: fill` renders as "fill (stretch)"). The walker needs the WIRE
                  // value — it is what it reports, and what it skips `launch_experience` by.
                  data-value={option}
                  className="relative flex cursor-default select-none items-center gap-2 rounded-sm py-1 pl-2 pr-7 text-[12px] outline-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground"
                >
                  {Icon ? <Icon className="size-3.5 shrink-0 text-muted-foreground" /> : null}
                  <SelectPrimitive.ItemText>{labels?.[option] ?? option}</SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator className="absolute right-2 flex items-center">
                    <Check className="size-3 text-indigo-400" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              );
            })}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

/** A Radix `Switch`, not a checkbox. A boolean in this panel is always a CAPABILITY being turned on
 *  (`autoplay`, `muted`, `loop`, `snap`, `decorative`) — a switch says "on/off", a checkbox says
 *  "selected", and the difference is the whole reason a settings panel uses switches.
 *
 *  ⚠️ ITS ROLE CHANGES: `checkbox` → `switch`. The walker's ROLES list carries `switch` now, and the
 *  inventory golden records the change. A role that moves without the walker knowing is a control
 *  that silently un-tests itself — which is the exact failure this golden exists to make loud. */
export function BoolInput({
  value,
  onChange,
  label,
}: {
  value: boolean | undefined;
  onChange: (value: boolean) => void;
  label: string;
}): React.JSX.Element {
  return (
    <SwitchPrimitive.Root
      aria-label={label}
      checked={value ?? false}
      onCheckedChange={onChange}
      className={cn(
        'peer inline-flex h-[18px] w-[30px] shrink-0 items-center rounded-full border border-transparent transition-colors',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        'data-[state=checked]:bg-indigo-500 data-[state=unchecked]:bg-muted'
      )}
    >
      <SwitchPrimitive.Thumb className="pointer-events-none block size-3.5 rounded-full bg-background shadow-sm ring-0 transition-transform data-[state=checked]:translate-x-[13px] data-[state=unchecked]:translate-x-[2px]" />
    </SwitchPrimitive.Root>
  );
}

// ---------------------------------------------------------------------------- tokens

const NONE = '__none__';

/** THE signature input. A colour is a token, never a hex — a raw value has no dark variant, so the
 *  wire forbids it (SPEC §10) and the picker IS the input rather than a convenience over one. The
 *  swatch shows BOTH halves of the pair, because that is what the token actually is. */
export function TokenPicker({
  label,
  kind,
  theme,
  value,
  onChange,
  optional,
  onMint,
  onVariant,
  description,
}: {
  /** REQUIRED. An unnamed `<select>` announces nothing to a screen reader, and tells the control
   *  inventory nothing about which control went missing when one does. */
  label: string;
  kind: 'colors' | 'fonts' | 'buttons';
  theme: Theme;
  value: string | null | undefined;
  onChange: (value: string | undefined) => void;
  optional?: boolean;
  /** Resolved token details stay available on hover without adding a variable-height subrow. */
  description?: string | undefined;
  /** MINT AND ASSIGN, in the caller's one `applyChange`. Present only for COLOURS, and the line is
   *  principled rather than arbitrary: a colour's missing dark half can be DERIVED. A one-off radius
   *  or elevation has nothing to derive, and a font token needs `line_height` and `max_scale`
   *  decisions that cannot be guessed — mint those wrong and you ship type that breaks at 2× Dynamic
   *  Type, silently. Those families get extract-on-demand instead. */
  onMint?: ((light: string, dark: string) => void) | undefined;
  /** Derive a NEW type style from the current one — "like `body`, but 18pt". Fonts only: unlike a
   *  colour there is nothing to derive, so the second type style is made explicitly rather than
   *  guessed, and it stays a named, reusable token instead of an inline value. */
  onVariant?: ((next: FontToken) => void) | undefined;
}): React.JSX.Element {
  const keys = Object.keys(theme[kind]);
  const current = value ?? NONE;

  // THE DESIGN HAND-OFF CALLS THIS "THE INSPECTOR'S SIGNATURE INPUT", and until now it was a bare
  // `<select>` of token NAMES — the one control in the panel whose entire job is to show you a
  // colour, showing you the word "surface" instead. A native `<option>` cannot carry a swatch; that
  // is the whole limitation, and it is why this is the second control to leave native behind.
  //
  // Now every option carries the TOKEN'S OWN PAIR — both halves, light and dark, because that is what
  // a colour token actually IS (SPEC §4). Picking one is a visual act again.
  return (
    <div
      data-token-control=""
      className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_32px_32px] items-center gap-1"
    >
      <SelectPrimitive.Root
        value={current}
        onValueChange={(next) => onChange(next === NONE ? undefined : next)}
      >
        <SelectPrimitive.Trigger
          aria-label={label}
          title={description}
          className={cn(CONTROL, 'flex min-w-0 flex-1 items-center gap-1.5')}
        >
          {value ? <ThemeTokenSample family={kind} token={value} theme={theme} compact /> : null}
          {/* BOUND, and it says so. These fields can hold NOTHING BUT a token — a `text`'s colour and
              font are token-only by law (SPEC §4) — so "bound" is the default rendering here, not a
              special case. Tinting it is what stops a token name reading as an ordinary string. */}
          <span className={cn('truncate', value && 'text-primary')}>{value ?? (optional ? 'none' : '—')}</span>
          <SelectPrimitive.Icon className="ml-auto shrink-0">
            <ChevronDown className="size-3 text-muted-foreground" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={4}
            align="end"
            className="z-50 max-h-[280px] min-w-[--radix-select-trigger-width] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          >
            <SelectPrimitive.Viewport>
            {optional || !keys.includes(current) ? (
              <SelectPrimitive.Item value={NONE} data-value={NONE} className={TOKEN_ITEM}>
                <span className="size-4 shrink-0 rounded-full border border-dashed" />
                <SelectPrimitive.ItemText>{optional ? 'none' : '—'}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ) : null}
            {keys.map((key) => (
              <SelectPrimitive.Item key={key} value={key} data-value={key} className={TOKEN_ITEM} title={`Preview ${key}`}>
                <ThemeTokenSample family={kind} token={key} theme={theme} />
                <SelectPrimitive.ItemText>{key}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="absolute right-2 flex items-center">
                  <Check className="size-3 text-indigo-400" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
      <div data-token-action="mint" className="grid size-8 place-items-center">
        {onMint ? <ColorMintPopover theme={theme} onMint={onMint} /> : null}
        {onVariant ? <FontVariantPopover token={value} theme={theme} onCreate={onVariant} /> : null}
        {!onMint && !onVariant ? <span aria-hidden="true" className="size-8" /> : null}
      </div>
      <div data-token-action="theme" className="grid size-8 place-items-center">
        {value ? <OpenThemeTokenLink family={kind} token={value} /> : <span aria-hidden="true" className="size-8" />}
      </div>
    </div>
  );
}

const TOKEN_ITEM =
  'relative flex cursor-default select-none items-center gap-2 rounded-sm py-1 pl-2 pr-7 text-[12px] outline-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground';

// ---------------------------------------------------------------------------- size

const modeOf = (value: SizeVal | undefined): SizeMode =>
  value === undefined ? 'auto' : value === 'fill' || value === 'hug' ? value : 'fixed';

// This dropdown is the ONE place the `hug`/`fill` vocabulary is introduced, and it used to show the
// bare wire words with no glyph — while the `fit` control three sections down was given full
// labels+icons precisely because "the word `fill` means three things in this panel". Same fix, same
// reason: the wire value is pinned in a binary and cannot be renamed, so the LABEL and a GLYPH carry
// the meaning. `auto` is our own concept (the axis key is absent) with no Figma analogue, so it is
// spelled out rather than iconified.
const SIZE_MODE_LABELS: Partial<Record<SizeMode, string>> = {
  auto: 'Auto',
  hug: 'Hug contents',
  fill: 'Fill container',
  fixed: 'Fixed',
};
/** WHICH FIELD KILLED THE BOUND. `sizeOptions` says WHETHER one may be set; this says who said no.
 *  Read off the width rather than guessed, and it names the fix, because "disabled" on its own leaves
 *  the author with nothing to change — the whole point of C2. */
function boundsInert(width: SizeVal | undefined): string {
  return typeof width === 'number'
    ? 'A fixed width is already the width — bounds apply to fill or hug.'
    : 'Set a width before bounding it.';
}

const SIZE_MODE_ICONS: Partial<Record<SizeMode, LucideIcon>> = {
  hug: Minimize2,
  fill: Maximize2,
  fixed: Ruler,
};

/** The legality of every option here is PROBED against `blockSchema` by `sizeOptions()` — this
 *  widget only renders the answer. It used to hand-copy the five size schemas (`modes()`/
 *  `canClear()`), which is how it came to offer values that could not publish. */
/** `height: fill` INSIDE A HUG-HEIGHT COLUMN IS ALWAYS ZERO, and nothing said so.
 *
 *  `measure.ts`'s `childHeight` returns 0 for a `fill` child when measuring a hug parent (SPEC §6.2),
 *  so the parent's height comes entirely from its OTHER children and `distributeMain` is left with
 *  `freeMain = 0` to divide. The node resolves to a ZERO-HEIGHT frame — verified against the solver:
 *  `[24, 0, 342, 0]`, full width and no height at all.
 *
 *  It does not look like a layout error, which is what makes it worth a hint. The glyphs still paint
 *  (no-shrink overflows LOUDLY rather than clipping, §6.2), so the text is there and reads normally —
 *  but the selection outline collapses to a hairline, the drag grip has nothing to sit on, and the
 *  next sibling starts at the same `y`. The reported symptom was "this block is not draggable".
 *
 *  ROWS AND BOXES ARE DELIBERATELY NOT FLAGGED. There the height is the CROSS axis, so a `fill` child
 *  stretches to the tallest sibling and genuinely does something. Only a column's MAIN axis has
 *  nothing to divide, and flagging the others would make the panel cry wolf. */
function inertFillHeight(node: BlockNode, parent: BlockNode | null): string | undefined {
  if (node.size?.height !== 'fill') return undefined;
  if (!parent || parent.block !== 'stack' || parent.direction !== 'column') return undefined;
  // `undefined` height means hug — the default, and the commonest way to land here.
  const parentHeight = parent.size?.height;
  if (parentHeight !== undefined && parentHeight !== 'hug') return undefined;
  return 'The parent stack hugs its height, so there is no free space to fill and this resolves to 0pt tall. Give the stack a height — a number, or `fill` — and this starts working.';
}

/** The resolved extent, in points. Shown for every mode, not just `fill`/`hug`: a FIXED 100 that
 *  reads `100pt` costs one glance to confirm, and the row that says `fill` and `0pt` is the one this
 *  exists for. */
function resolved(
  frame: readonly [number, number, number, number] | null | undefined,
  axis: 'width' | 'height'
): string | undefined {
  if (!frame) return undefined;
  const value = axis === 'width' ? frame[2] : frame[3];
  return `${Math.round(value * 10) / 10}pt`;
}

export function SizeField({
  node,
  parent,
  step,
  size,
  onChange,
  errors,
  frame,
}: {
  node: BlockNode;
  parent: BlockNode | null;
  /** The step this node lives in. The ROOT's height law needs the SURFACE, and only the document can
   *  see the surface and the root together (SPEC §6.1c). */
  step?: StepCtx | undefined;
  size: Size | undefined;
  /** `field` names the AXIS that changed, and it becomes the undo merge key. Without it every size
   *  edit shares one key, so setting a width and then a height coalesce into a SINGLE undo entry and
   *  ⌘Z jumps back past both. Two deliberate edits are two undo steps. */
  onChange: (size: Size | undefined, field?: string) => void;
  /** WHAT THE SOLVER ACTUALLY GAVE IT. `fill` and `hug` are promises the solver keeps, and the panel
   *  could only ever repeat the promise — so a `fill` that resolved to 0pt (a fill child of a
   *  hug-height column, SPEC §6.2) read as an ordinary row with a broken selection. We own the
   *  solver, so this is the authoritative number, not a value read back out of the browser. */
  frame?: readonly [number, number, number, number] | null | undefined;
  errors: {
    width?: string | undefined;
    height?: string | undefined;
    max_width?: string | undefined;
    min_width?: string | undefined;
  };
}): React.JSX.Element {
  const legality = sizeOptions(node, parent, step);

  const axis = (name: 'width' | 'height'): React.JSX.Element => {
    const value = name === 'width' ? size?.width : size?.height;
    const options = legality[name];

    const set = (next: SizeVal | undefined): void => {
      // Clearing the WIDTH clears the whole object — `width` is the only non-optional member of
      // `Size`. That is only ever reachable when nothing else is set, because `sizeOptions` probes
      // the same candidate and stops offering `auto` the moment a height exists (it would delete it).
      if (name === 'width') {
        if (next === undefined) return onChange(undefined, `size.${name}`);
        return onChange({ ...size, width: next }, `size.${name}`);
      }
      // SPREAD, then delete. This used to rebuild `{width, height}` from scratch, which silently
      // dropped every other member of Size — today that is max_width/min_width, tomorrow whatever
      // else lands here.
      const patch: Size = { ...size, width: size?.width ?? 'fill', height: next };
      if (next === undefined) delete patch.height;
      onChange(patch, `size.${name}`);
    };

    return (
      <Field
        key={name}
        label={name}
        error={errors[name]}
        set={value !== undefined}
        inert={name === 'height' ? inertFillHeight(node, parent) : undefined}
        note={resolved(frame, name)}
      >
        <SelectInput
          className={typeof value === 'number' ? 'w-[80px]' : ''}
          aria-label={`${name} mode`}
          value={modeOf(value)}
          options={options}
          labels={SIZE_MODE_LABELS}
          icons={SIZE_MODE_ICONS}
          onChange={(mode) =>
            set(mode === 'auto' ? undefined : mode === 'fixed' ? (typeof value === 'number' ? value : 100) : mode)
          }
        />
        {typeof value === 'number' ? (
          <NumberInput
            className="w-[62px]"
            aria-label={name}
            min={0}
            value={value}
            onChange={(n) => set(n ?? 0)}
          />
        ) : null}
      </Field>
    );
  };

  /** A bound on the RESOLVED width (SPEC §6.1b). A WIDTH law — there is no height bound, because
   *  height is derived (from text wrap, from `aspect_ratio`) and a height clamp would silently
   *  truncate content rather than bound a box.
   *
   *  IT NO LONGER DISAPPEARS, and that was a bug of ours rather than a missing feature. The row used
   *  to be REMOVED whenever the schema rejected it — which is exactly what C2 forbids for a field
   *  neutered by ANOTHER field's value. An author with a fixed width went looking for `max width`,
   *  found nothing at all, and had no way to learn that their own width was the reason. `Field.inert`
   *  exists for precisely this, and the panel was applying its own law to fields while breaking it
   *  for rows.
   *
   *  AN INERT BOUND IS ALWAYS EMPTY — guaranteed, not assumed. `sizeOptions` probes each width mode
   *  against the WHOLE size object, so `fixed` is never offered while a bound is set; no mode switch
   *  reachable through this panel can strand a value here. */
  const bound = (name: 'max_width' | 'min_width'): React.JSX.Element => {
    const label = name.replace('_', ' ');
    const inert = legality.bounds ? undefined : boundsInert(size?.width);
    return (
      <Field key={name} label={label} error={errors[name]} set={size?.[name] !== undefined} inert={inert}>
        <NumberInput
          className="w-[62px]"
          aria-label={label}
          min={0}
          value={size?.[name]}
          disabled={inert !== undefined}
          onChange={(n) => {
            if (!size) return;
            const patch: Size = { ...size, [name]: n };
            if (n === undefined) delete patch[name];
            onChange(patch, `size.${name}`);
          }}
        />
      </Field>
    );
  };

  // `max_width` and `min_width` DO NOT EXIST ON SCREEN until you have used them. Plasmic's
  // `SizeSection` is the canonical case for progressive disclosure and it is exactly ours: two rows
  // that almost nobody sets, sitting in everybody's panel forever.
  return (
    <>
      {axis('width')}
      {axis('height')}
      <Disclosure>
        {bound('max_width')}
        {bound('min_width')}
      </Disclosure>
    </>
  );
}

// ---------------------------------------------------------------------------- padding

const SIDES = ['top', 'end', 'bottom', 'start'] as const;

export function PaddingField({
  padding,
  onChange,
}: {
  padding: Padding | undefined;
  onChange: (padding: Padding | undefined) => void;
}): React.JSX.Element {
  const current: Padding = padding ?? { top: 0, end: 0, bottom: 0, start: 0 };
  const all = (patch: Partial<Padding>): void => {
    const next = { ...current, ...patch };
    // All-zero padding IS no padding — keeping `{0,0,0,0}` on every block would bloat every bundle
    // with a field that means nothing.
    onChange(SIDES.every((s) => next[s] === 0) ? undefined : next);
  };

  return <PaddingBox current={current} padding={padding} all={all} />;
}

/** Four stable two-column inputs, with the link control on its own fixed rail. This mirrors the
 *  spatial order without making the compact inspector spend three rows on a miniature box diagram.
 *  Linking all four remains the overwhelmingly common case.
 *
 *  `start`/`end`, never `left`/`right`: padding MIRRORS under RTL (SPEC §5.1), so naming it by
 *  screen side would be a lie on half the world's devices. The box places them where they land in
 *  LTR and says `start`/`end` out loud in the label. */
function PaddingBox({
  current,
  padding,
  all,
}: {
  current: Padding;
  padding: Padding | undefined;
  all: (patch: Partial<Padding>) => void;
}): React.JSX.Element {
  const [linked, setLinked] = useState(
    padding !== undefined && SIDES.every((s) => current[s] === current.top)
  );

  const write = (side: (typeof SIDES)[number], n: number | undefined): void =>
    all(linked ? { top: n ?? 0, end: n ?? 0, bottom: n ?? 0, start: n ?? 0 } : { [side]: n ?? 0 });

  const cell = (side: (typeof SIDES)[number]): React.JSX.Element => (
    <NumberInput
      className="w-full"
      aria-label={`padding ${side}`}
      min={0}
      value={current[side]}
      onChange={(n) => write(side, n)}
    />
  );

  return (
    <Field label="Padding" stacked set={padding !== undefined} onUnset={() => all({ top: 0, end: 0, bottom: 0, start: 0 })}>
      <div className="grid w-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)_32px] grid-rows-2 gap-1">
        <div className="relative">
          <span className="pointer-events-none absolute left-2 top-1/2 z-10 -translate-y-1/2 text-[10px] font-medium text-muted-foreground">T</span>
          {cell('top')}
        </div>
        <div className="relative">
          <span className="pointer-events-none absolute left-2 top-1/2 z-10 -translate-y-1/2 text-[10px] font-medium text-muted-foreground">E</span>
          {cell('end')}
        </div>
        <button
          type="button"
          role="checkbox"
          aria-checked={linked}
          aria-label="link padding"
          title={linked ? 'editing all four sides' : 'link all four sides'}
          onClick={() => {
            const next = !linked;
            setLinked(next);
            if (next) all({ top: current.top, end: current.top, bottom: current.top, start: current.top });
          }}
          className={cn(
            'row-span-2 flex h-full items-center justify-center rounded-lg border text-[11px] outline-none focus-visible:ring-1 focus-visible:ring-ring',
            linked ? 'border-indigo-500 text-indigo-400' : 'text-muted-foreground'
          )}
        >
          {linked ? <Link2 className="size-3.5" /> : <Unlink2 className="size-3.5" />}
        </button>
        <div className="relative">
          <span className="pointer-events-none absolute left-2 top-1/2 z-10 -translate-y-1/2 text-[10px] font-medium text-muted-foreground">S</span>
          {cell('start')}
        </div>
        <div className="relative">
          <span className="pointer-events-none absolute left-2 top-1/2 z-10 -translate-y-1/2 text-[10px] font-medium text-muted-foreground">B</span>
          {cell('bottom')}
        </div>
      </div>
    </Field>
  );
}

// ---------------------------------------------------------------------------- spans

/** Spans, not a template string. `{{path}}` would force four mini-parsers into pinned binaries
 *  (SPEC §11.1) — so a `ref` is a structured span with a REQUIRED fallback, and the editor asks for
 *  it at the moment the span is created. Inline canvas editing with variable chips is E3; this list
 *  is the E1 surface for exactly the same data. */
export function SpansField({
  spans,
  onChange,
  errors,
}: {
  spans: Span[];
  onChange: (spans: Span[]) => void;
  errors: Record<string, string>;
}): React.JSX.Element {
  const replace = (index: number, span: Span): void =>
    onChange(spans.map((s, i) => (i === index ? span : s)));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">spans</span>
        <span className="flex gap-1">
          <button
            type="button"
            className="rounded border px-1.5 py-0.5 text-[11px] hover:bg-secondary"
            onClick={() => onChange([...spans, { kind: 'lit', value: 'Text' }])}
          >
            + text
          </button>
          <button
            type="button"
            className="rounded border px-1.5 py-0.5 text-[11px] hover:bg-secondary"
            onClick={() => onChange([...spans, { kind: 'ref', path: 'user.name', fallback: 'there' }])}
          >
            + variable
          </button>
        </span>
      </div>

      {spans.map((span, index) => (
        <div key={index} className="space-y-1 rounded-md border p-1.5">
          <div className="flex items-center gap-1">
            {span.kind === 'lit' ? (
              <TextInput
                className="flex-1"
                aria-label={`span ${index} text`}
                value={span.value}
                onChange={(value) => replace(index, { kind: 'lit', value })}
              />
            ) : (
              <TextInput
                className="flex-1"
                aria-label={`span ${index} path`}
                value={span.path}
                onChange={(path) => replace(index, { ...span, path })}
              />
            )}
            <button
              type="button"
              aria-label={`Remove span ${index}`}
              className="p-1 text-muted-foreground hover:text-destructive disabled:opacity-30"
              disabled={spans.length === 1}
              onClick={() => onChange(spans.filter((_, i) => i !== index))}
            >
              <X className="size-3" />
            </button>
          </div>

          {span.kind === 'ref' ? (
            <Field label="fallback" error={errors[`spans.${index}.fallback`]}>
              <TextInput
                className="w-[130px]"
                aria-label={`span ${index} fallback`}
                value={span.fallback}
                onChange={(fallback) => replace(index, { ...span, fallback })}
              />
            </Field>
          ) : null}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------- custom config

/** The host's own struct — we hand it the raw blob and never type it (SPEC §8). Parsed on every
 *  keystroke but only COMMITTED when it parses, so a half-typed brace does not blank the block. */
export function ConfigField({
  config,
  onChange,
}: {
  config: Record<string, unknown> | undefined;
  onChange: (config: Record<string, unknown> | undefined) => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState(() => (config ? JSON.stringify(config, null, 2) : ''));
  const [error, setError] = useState<string | undefined>(undefined);

  return (
    <div className="space-y-1">
      <label className="text-[11px] text-muted-foreground" htmlFor="config">
        config (JSON)
      </label>
      <textarea
        id="config"
        aria-label="config"
        rows={4}
        className="w-full rounded-md border bg-transparent p-1.5 font-mono text-[11px] outline-none focus:border-indigo-500"
        value={draft}
        onChange={(e) => {
          const next = e.target.value;
          setDraft(next);
          if (next.trim() === '') {
            setError(undefined);
            return onChange(undefined);
          }
          try {
            const parsed: unknown = JSON.parse(next);
            if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
              return setError('config must be a JSON object');
            }
            setError(undefined);
            onChange(parsed as Record<string, unknown>);
          } catch {
            setError('invalid JSON');
          }
        }}
      />
      {error ? <p className="text-[11px] text-destructive">{error}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------- requires

/** The capability gate (SPEC §11.5). An unsatisfied `requires` sends the block to its `fallback` —
 *  which is why the inspector nags for one when this is set. */
export function RequiresField({
  requires,
  onChange,
  set,
}: {
  requires: string[] | undefined;
  onChange: (requires: string[] | undefined) => void;
  /** Passed in rather than derived, because `Disclosure` reads it off THIS element's props to
   *  decide whether the row is on screen at all — it cannot see inside the component. */
  set?: boolean;
}): React.JSX.Element {
  return (
    <Field label="requires" set={set}>
      <TextInput
        aria-label="requires"
        placeholder="video, lottie"
        value={(requires ?? []).join(', ')}
        onChange={(value) => {
          const list = value
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          onChange(list.length > 0 ? list : undefined);
        }}
      />
    </Field>
  );
}

export { Plus };
